/**
 * FikoSPH Material Library
 *
 * Comprehensive fluid material database with 20+ preset materials
 * Supports custom material creation, temperature-dependent viscosity,
 * and JSON import/export for custom materials.
 *
 * Physical properties are based on real-world engineering data.
 */

import type { Vec3, Renk } from './FikoSPHParametreler';

/**
 * Material Category Enumeration
 */
export enum MalzemeKategori {
  SU_BAZLI = 'su_bazli',
  YAG_BAZLI = 'yag_bazli',
  METAL_ERIYIK = 'metal_eriyik',
  POLIMER = 'polimer',
  GIDA = 'gida',
  KIMYASAL = 'kimyasal',
  DIGER = 'diger',
  OZEL = 'ozel',
}

/**
 * Fluid Material Interface
 */
export interface SiviMalzeme {
  id: string;               // Unique ID
  ad: string;               // Display name (Turkish)
  adEN: string;             // English name (for export)
  kategori: MalzemeKategori;

  // Physical properties
  yogunluk: number;         // kg/m^3 (density)
  viskozite: number;        // Pa.s (dynamic viscosity)
  yuzeyGerilimi: number;    // N/m (surface tension)

  // Thermal properties
  sicaklik: number;         // °C (default temperature)
  ozguIsı: number;          // J/(kg·K) (specific heat capacity)
  isiIletkenlik: number;    // W/(m·K) (thermal conductivity)
  solidusSicaklik: number;  // °C (solidification start, -999 = no solidification)
  liquidusSicaklik: number; // °C (fully liquid temp)

  // Visual properties
  renk: Renk;               // RGBA color
  saydamlik: number;        // 0-1 (transparency for fluid render)
  kirilmaIndisi: number;    // IOR (index of refraction, water=1.33)
  absorpsiyonRenk: Renk;    // Beer's law absorption color (deep color)
  absorpsiyonMesafe: number;// meters (how deep before full absorption color)

  // Temperature-dependent viscosity (Arrhenius model)
  // mu(T) = mu_ref * exp(B * (1/T - 1/T_ref))
  sicaklikBagimliligi: boolean;    // Enable temp-dependent viscosity
  arrheniusB: number;              // Arrhenius B coefficient
  arrheniusRefSicaklik: number;    // Reference temperature (°C)

  // Custom flag
  ozel: boolean;            // true = user-created, false = preset

  // Casting-specific properties
  dokumOzellikleri?: {
    cekmeOrani: number;
    akiskanlikIndeksi: number;
    sicakYirtilmaEgilimi: number;
    gazCozunurlugu: number;
    uygunDokumTurleri: string[];
    onerDokumSicaklik: number;
    onerKalipSicaklik: number;
    isiIletkenlikKati: number;
    partitionK?: number;
  };
}

/**
 * Preset Material Database - 21 Realistic Fluid Materials
 */
const PRESET_MALZEMELER: SiviMalzeme[] = [
  // 1. Water (Room Temperature)
  {
    id: 'su',
    ad: 'Su',
    adEN: 'Water',
    kategori: MalzemeKategori.SU_BAZLI,
    yogunluk: 998.2,
    viskozite: 0.001002,
    yuzeyGerilimi: 0.0728,
    sicaklik: 20.0,
    ozguIsı: 4182.0,
    isiIletkenlik: 0.598,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.2, g: 0.5, b: 0.9, a: 1.0 },
    saydamlik: 0.85,
    kirilmaIndisi: 1.333,
    absorpsiyonRenk: { r: 0.0, g: 0.1, b: 0.3, a: 1.0 },
    absorpsiyonMesafe: 3.0,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 20.0,
    ozel: false
  },

  // 2. Hot Water
  {
    id: 'sicak_su',
    ad: 'Sıcak Su',
    adEN: 'Hot Water',
    kategori: MalzemeKategori.SU_BAZLI,
    yogunluk: 960.6,
    viskozite: 0.0003565,
    yuzeyGerilimi: 0.0626,
    sicaklik: 80.0,
    ozguIsı: 4196.0,
    isiIletkenlik: 0.670,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.3, g: 0.6, b: 1.0, a: 1.0 },
    saydamlik: 0.9,
    kirilmaIndisi: 1.325,
    absorpsiyonRenk: { r: 0.1, g: 0.2, b: 0.4, a: 1.0 },
    absorpsiyonMesafe: 3.5,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 80.0,
    ozel: false
  },

  // 3. Olive Oil
  {
    id: 'zeytin_yagi',
    ad: 'Yağ (Zeytin)',
    adEN: 'Olive Oil',
    kategori: MalzemeKategori.YAG_BAZLI,
    yogunluk: 920.0,
    viskozite: 0.081,
    yuzeyGerilimi: 0.032,
    sicaklik: 20.0,
    ozguIsı: 1970.0,
    isiIletkenlik: 0.17,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.85, g: 0.75, b: 0.2, a: 1.0 },
    saydamlik: 0.7,
    kirilmaIndisi: 1.467,
    absorpsiyonRenk: { r: 0.6, g: 0.5, b: 0.0, a: 1.0 },
    absorpsiyonMesafe: 0.5,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 20.0,
    ozel: false
  },

  // 4. Honey
  {
    id: 'bal',
    ad: 'Bal',
    adEN: 'Honey',
    kategori: MalzemeKategori.GIDA,
    yogunluk: 1400.0,
    viskozite: 2.0,
    yuzeyGerilimi: 0.050,
    sicaklik: 20.0,
    ozguIsı: 2200.0,
    isiIletkenlik: 0.5,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.9, g: 0.65, b: 0.1, a: 1.0 },
    saydamlik: 0.6,
    kirilmaIndisi: 1.494,
    absorpsiyonRenk: { r: 0.7, g: 0.4, b: 0.0, a: 1.0 },
    absorpsiyonMesafe: 0.2,
    sicaklikBagimliligi: true,
    arrheniusB: 4500.0,
    arrheniusRefSicaklik: 20.0,
    ozel: false
  },

  // 5. Chocolate (Melted)
  {
    id: 'cikolata',
    ad: 'Çikolata',
    adEN: 'Chocolate',
    kategori: MalzemeKategori.GIDA,
    yogunluk: 1200.0,
    viskozite: 1.5,
    yuzeyGerilimi: 0.040,
    sicaklik: 40.0,
    ozguIsı: 1600.0,
    isiIletkenlik: 0.4,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.35, g: 0.2, b: 0.1, a: 1.0 },
    saydamlik: 0.3,
    kirilmaIndisi: 1.52,
    absorpsiyonRenk: { r: 0.2, g: 0.1, b: 0.05, a: 1.0 },
    absorpsiyonMesafe: 0.1,
    sicaklikBagimliligi: true,
    arrheniusB: 3800.0,
    arrheniusRefSicaklik: 40.0,
    ozel: false
  },

  // 6. Gasoline
  {
    id: 'benzin',
    ad: 'Benzin',
    adEN: 'Gasoline',
    kategori: MalzemeKategori.KIMYASAL,
    yogunluk: 750.0,
    viskozite: 0.0006,
    yuzeyGerilimi: 0.022,
    sicaklik: 20.0,
    ozguIsı: 2220.0,
    isiIletkenlik: 0.14,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.95, g: 0.9, b: 0.5, a: 1.0 },
    saydamlik: 0.95,
    kirilmaIndisi: 1.42,
    absorpsiyonRenk: { r: 0.8, g: 0.7, b: 0.2, a: 1.0 },
    absorpsiyonMesafe: 1.0,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 20.0,
    ozel: false
  },

  // 7. Mercury
  {
    id: 'civa',
    ad: 'Civa',
    adEN: 'Mercury',
    kategori: MalzemeKategori.METAL_ERIYIK,
    yogunluk: 13534.0,
    viskozite: 0.001526,
    yuzeyGerilimi: 0.4865,
    sicaklik: 20.0,
    ozguIsı: 139.5,
    isiIletkenlik: 8.3,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.75, g: 0.75, b: 0.75, a: 1.0 },
    saydamlik: 0.0,
    kirilmaIndisi: 1.62,
    absorpsiyonRenk: { r: 0.5, g: 0.5, b: 0.5, a: 1.0 },
    absorpsiyonMesafe: 0.01,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 20.0,
    ozel: false
  },

  // 8. Lava
  {
    id: 'lav',
    ad: 'Lav',
    adEN: 'Lava',
    kategori: MalzemeKategori.DIGER,
    yogunluk: 2700.0,
    viskozite: 500.0,
    yuzeyGerilimi: 0.400,
    sicaklik: 1100.0,
    ozguIsı: 1150.0,
    isiIletkenlik: 3.5,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 1.0, g: 0.3, b: 0.0, a: 1.0 },
    saydamlik: 0.0,
    kirilmaIndisi: 1.6,
    absorpsiyonRenk: { r: 0.5, g: 0.0, b: 0.0, a: 1.0 },
    absorpsiyonMesafe: 0.05,
    sicaklikBagimliligi: true,
    arrheniusB: 12000.0,
    arrheniusRefSicaklik: 1100.0,
    ozel: false
  },

  // 9. Molten Aluminum
  {
    id: 'aluminyum_eriyik',
    ad: 'Alüminyum (Eriyik)',
    adEN: 'Molten Aluminum',
    kategori: MalzemeKategori.METAL_ERIYIK,
    yogunluk: 2375.0,
    viskozite: 0.00113,
    yuzeyGerilimi: 0.871,
    sicaklik: 700.0,
    ozguIsı: 1177.0,
    isiIletkenlik: 91.0,
    solidusSicaklik: 577.0,
    liquidusSicaklik: 660.0,
    renk: { r: 0.95, g: 0.95, b: 0.9, a: 1.0 },
    saydamlik: 0.0,
    kirilmaIndisi: 1.44,
    absorpsiyonRenk: { r: 0.7, g: 0.7, b: 0.7, a: 1.0 },
    absorpsiyonMesafe: 0.01,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 700.0,
    ozel: false,
    dokumOzellikleri: {
      cekmeOrani: 0.065,
      akiskanlikIndeksi: 75,
      sicakYirtilmaEgilimi: 0.3,
      gazCozunurlugu: 0.69,
      uygunDokumTurleri: ['kum', 'basincli', 'kokil', 'hassas', 'lpdc'],
      onerDokumSicaklik: 720,
      onerKalipSicaklik: 200,
      isiIletkenlikKati: 237,
      partitionK: 0.13,
    },
  },

  // 10. Molten Iron
  {
    id: 'demir_eriyik',
    ad: 'Demir (Eriyik)',
    adEN: 'Molten Iron',
    kategori: MalzemeKategori.METAL_ERIYIK,
    yogunluk: 7020.0,
    viskozite: 0.006,
    yuzeyGerilimi: 1.872,
    sicaklik: 1600.0,
    ozguIsı: 824.0,
    isiIletkenlik: 41.0,
    solidusSicaklik: 1147.0,
    liquidusSicaklik: 1538.0,
    renk: { r: 0.9, g: 0.2, b: 0.1, a: 1.0 },
    saydamlik: 0.0,
    kirilmaIndisi: 1.51,
    absorpsiyonRenk: { r: 0.5, g: 0.1, b: 0.0, a: 1.0 },
    absorpsiyonMesafe: 0.01,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 1600.0,
    ozel: false,
    dokumOzellikleri: {
      cekmeOrani: 0.04,
      akiskanlikIndeksi: 120,
      sicakYirtilmaEgilimi: 0.15,
      gazCozunurlugu: 0.001,
      uygunDokumTurleri: ['kum', 'kokil'],
      onerDokumSicaklik: 1400,
      onerKalipSicaklik: 25,
      isiIletkenlikKati: 50,
    },
  },

  // 11. Molten Copper
  {
    id: 'bakir_eriyik',
    ad: 'Bakır (Eriyik)',
    adEN: 'Molten Copper',
    kategori: MalzemeKategori.METAL_ERIYIK,
    yogunluk: 8000.0,
    viskozite: 0.004,
    yuzeyGerilimi: 1.285,
    sicaklik: 1150.0,
    ozguIsı: 495.0,
    isiIletkenlik: 170.0,
    solidusSicaklik: 1065.0,
    liquidusSicaklik: 1085.0,
    renk: { r: 1.0, g: 0.5, b: 0.2, a: 1.0 },
    saydamlik: 0.0,
    kirilmaIndisi: 1.48,
    absorpsiyonRenk: { r: 0.7, g: 0.3, b: 0.0, a: 1.0 },
    absorpsiyonMesafe: 0.01,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 1150.0,
    ozel: false,
    dokumOzellikleri: {
      cekmeOrani: 0.05,
      akiskanlikIndeksi: 90,
      sicakYirtilmaEgilimi: 0.25,
      gazCozunurlugu: 0.01,
      uygunDokumTurleri: ['kum', 'kokil', 'savurma'],
      onerDokumSicaklik: 1200,
      onerKalipSicaklik: 200,
      isiIletkenlikKati: 398,
    },
  },

  // 12. Epoxy Resin
  {
    id: 'epoksi_recine',
    ad: 'Epoksi Reçine',
    adEN: 'Epoxy Resin',
    kategori: MalzemeKategori.POLIMER,
    yogunluk: 1100.0,
    viskozite: 5.0,
    yuzeyGerilimi: 0.035,
    sicaklik: 25.0,
    ozguIsı: 1050.0,
    isiIletkenlik: 0.2,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.95, g: 0.9, b: 0.6, a: 1.0 },
    saydamlik: 0.8,
    kirilmaIndisi: 1.55,
    absorpsiyonRenk: { r: 0.8, g: 0.7, b: 0.4, a: 1.0 },
    absorpsiyonMesafe: 0.3,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 25.0,
    ozel: false
  },

  // 13. Fresh Concrete
  {
    id: 'beton_taze',
    ad: 'Beton (Taze)',
    adEN: 'Fresh Concrete',
    kategori: MalzemeKategori.DIGER,
    yogunluk: 2400.0,
    viskozite: 50.0,
    yuzeyGerilimi: 0.001,
    sicaklik: 20.0,
    ozguIsı: 880.0,
    isiIletkenlik: 1.4,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.5, g: 0.5, b: 0.5, a: 1.0 },
    saydamlik: 0.0,
    kirilmaIndisi: 1.52,
    absorpsiyonRenk: { r: 0.3, g: 0.3, b: 0.3, a: 1.0 },
    absorpsiyonMesafe: 0.05,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 20.0,
    ozel: false
  },

  // 14. Blood
  {
    id: 'kan',
    ad: 'Kan',
    adEN: 'Blood',
    kategori: MalzemeKategori.GIDA,
    yogunluk: 1060.0,
    viskozite: 0.0035,
    yuzeyGerilimi: 0.058,
    sicaklik: 37.0,
    ozguIsı: 3617.0,
    isiIletkenlik: 0.52,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.6, g: 0.0, b: 0.0, a: 1.0 },
    saydamlik: 0.2,
    kirilmaIndisi: 1.35,
    absorpsiyonRenk: { r: 0.3, g: 0.0, b: 0.0, a: 1.0 },
    absorpsiyonMesafe: 0.05,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 37.0,
    ozel: false
  },

  // 15. Milk
  {
    id: 'sut',
    ad: 'Süt',
    adEN: 'Milk',
    kategori: MalzemeKategori.GIDA,
    yogunluk: 1030.0,
    viskozite: 0.002,
    yuzeyGerilimi: 0.050,
    sicaklik: 4.0,
    ozguIsı: 3900.0,
    isiIletkenlik: 0.57,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 1.0, g: 1.0, b: 0.95, a: 1.0 },
    saydamlik: 0.1,
    kirilmaIndisi: 1.345,
    absorpsiyonRenk: { r: 0.9, g: 0.9, b: 0.85, a: 1.0 },
    absorpsiyonMesafe: 0.02,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 4.0,
    ozel: false
  },

  // 16. Glycerin
  {
    id: 'gliserin',
    ad: 'Gliserin',
    adEN: 'Glycerin',
    kategori: MalzemeKategori.KIMYASAL,
    yogunluk: 1261.0,
    viskozite: 1.412,
    yuzeyGerilimi: 0.063,
    sicaklik: 20.0,
    ozguIsı: 2427.0,
    isiIletkenlik: 0.285,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.98, g: 0.98, b: 0.98, a: 1.0 },
    saydamlik: 0.95,
    kirilmaIndisi: 1.473,
    absorpsiyonRenk: { r: 0.95, g: 0.95, b: 0.95, a: 1.0 },
    absorpsiyonMesafe: 1.0,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 20.0,
    ozel: false
  },

  // 17. Acetic Acid
  {
    id: 'asetik_asit',
    ad: 'Asetik Asit',
    adEN: 'Acetic Acid',
    kategori: MalzemeKategori.KIMYASAL,
    yogunluk: 1049.0,
    viskozite: 0.00122,
    yuzeyGerilimi: 0.0276,
    sicaklik: 20.0,
    ozguIsı: 2090.0,
    isiIletkenlik: 0.19,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.97, g: 0.97, b: 0.97, a: 1.0 },
    saydamlik: 0.98,
    kirilmaIndisi: 1.372,
    absorpsiyonRenk: { r: 0.95, g: 0.95, b: 0.95, a: 1.0 },
    absorpsiyonMesafe: 1.5,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 20.0,
    ozel: false
  },

  // 18. Motor Oil
  {
    id: 'motor_yagi',
    ad: 'Motor Yağı',
    adEN: 'Motor Oil',
    kategori: MalzemeKategori.YAG_BAZLI,
    yogunluk: 880.0,
    viskozite: 0.2,
    yuzeyGerilimi: 0.034,
    sicaklik: 40.0,
    ozguIsı: 1880.0,
    isiIletkenlik: 0.145,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.25, g: 0.15, b: 0.05, a: 1.0 },
    saydamlik: 0.4,
    kirilmaIndisi: 1.475,
    absorpsiyonRenk: { r: 0.15, g: 0.08, b: 0.0, a: 1.0 },
    absorpsiyonMesafe: 0.1,
    sicaklikBagimliligi: true,
    arrheniusB: 3200.0,
    arrheniusRefSicaklik: 40.0,
    ozel: false
  },

  // 19. Silicone Oil
  {
    id: 'silikon_yagi',
    ad: 'Silikon Yağı',
    adEN: 'Silicone Oil',
    kategori: MalzemeKategori.YAG_BAZLI,
    yogunluk: 960.0,
    viskozite: 0.1,
    yuzeyGerilimi: 0.021,
    sicaklik: 25.0,
    ozguIsı: 1460.0,
    isiIletkenlik: 0.15,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 0.98, g: 0.98, b: 0.98, a: 1.0 },
    saydamlik: 0.97,
    kirilmaIndisi: 1.403,
    absorpsiyonRenk: { r: 0.95, g: 0.95, b: 0.95, a: 1.0 },
    absorpsiyonMesafe: 0.8,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 25.0,
    ozel: false
  },

  // 20. Molten Tin
  {
    id: 'kalay_eriyik',
    ad: 'Kalay (Eriyik)',
    adEN: 'Molten Tin',
    kategori: MalzemeKategori.METAL_ERIYIK,
    yogunluk: 6980.0,
    viskozite: 0.00185,
    yuzeyGerilimi: 0.560,
    sicaklik: 250.0,
    ozguIsı: 243.0,
    isiIletkenlik: 32.0,
    solidusSicaklik: 183.0,
    liquidusSicaklik: 232.0,
    renk: { r: 0.85, g: 0.85, b: 0.85, a: 1.0 },
    saydamlik: 0.0,
    kirilmaIndisi: 1.46,
    absorpsiyonRenk: { r: 0.6, g: 0.6, b: 0.6, a: 1.0 },
    absorpsiyonMesafe: 0.01,
    sicaklikBagimliligi: false,
    arrheniusB: 0,
    arrheniusRefSicaklik: 250.0,
    ozel: false,
    dokumOzellikleri: {
      cekmeOrani: 0.035,
      akiskanlikIndeksi: 100,
      sicakYirtilmaEgilimi: 0.1,
      gazCozunurlugu: 0.001,
      uygunDokumTurleri: ['kum', 'basincli'],
      onerDokumSicaklik: 280,
      onerKalipSicaklik: 25,
      isiIletkenlikKati: 67,
    },
  },

  // 21. Molten Glass
  {
    id: 'cam_eriyik',
    ad: 'Cam (Eriyik)',
    adEN: 'Molten Glass',
    kategori: MalzemeKategori.DIGER,
    yogunluk: 2500.0,
    viskozite: 100.0,
    yuzeyGerilimi: 0.300,
    sicaklik: 1400.0,
    ozguIsı: 1050.0,
    isiIletkenlik: 1.05,
    solidusSicaklik: -999,
    liquidusSicaklik: -999,
    renk: { r: 1.0, g: 0.5, b: 0.2, a: 1.0 },
    saydamlik: 0.3,
    kirilmaIndisi: 1.52,
    absorpsiyonRenk: { r: 0.8, g: 0.3, b: 0.0, a: 1.0 },
    absorpsiyonMesafe: 0.2,
    sicaklikBagimliligi: true,
    arrheniusB: 15000.0,
    arrheniusRefSicaklik: 1400.0,
    ozel: false
  },
];

/**
 * Material Library Class
 *
 * Manages preset and custom fluid materials with full CRUD operations,
 * temperature-dependent viscosity calculations, and JSON import/export.
 */
export class MalzemeKutuphanesi {
  private malzemeler: Map<string, SiviMalzeme> = new Map();

  constructor() {
    // Load all preset materials
    for (const m of PRESET_MALZEMELER) {
      this.malzemeler.set(m.id, m);
    }
  }

  /**
   * Get material by ID
   */
  al(id: string): SiviMalzeme | undefined {
    return this.malzemeler.get(id);
  }

  /**
   * Get all materials
   */
  hepsi(): SiviMalzeme[] {
    return Array.from(this.malzemeler.values());
  }

  /**
   * Get materials by category
   */
  kategoriGore(kategori: MalzemeKategori): SiviMalzeme[] {
    return this.hepsi().filter(m => m.kategori === kategori);
  }

  /**
   * Get only preset materials
   */
  presetler(): SiviMalzeme[] {
    return this.hepsi().filter(m => !m.ozel);
  }

  /**
   * Get only custom materials
   */
  ozeller(): SiviMalzeme[] {
    return this.hepsi().filter(m => m.ozel);
  }

  /**
   * Add custom material
   */
  ozelEkle(malzeme: SiviMalzeme): void {
    malzeme.ozel = true;
    this.malzemeler.set(malzeme.id, malzeme);
  }

  /**
   * Remove custom material (can't remove presets)
   */
  ozelSil(id: string): boolean {
    const m = this.malzemeler.get(id);
    if (m && m.ozel) {
      return this.malzemeler.delete(id);
    }
    return false;
  }

  /**
   * Create a new custom material with water-based defaults
   */
  yeniOzelMalzeme(ad: string): SiviMalzeme {
    const id = 'ozel_' + Date.now();
    const yeniMalzeme: SiviMalzeme = {
      id: id,
      ad: ad,
      adEN: ad,
      kategori: MalzemeKategori.OZEL,
      yogunluk: 1000.0,
      viskozite: 0.001,
      yuzeyGerilimi: 0.072,
      sicaklik: 20.0,
      ozguIsı: 4200.0,
      isiIletkenlik: 0.6,
      solidusSicaklik: -999,
      liquidusSicaklik: -999,
      renk: { r: 0.5, g: 0.5, b: 0.8, a: 1.0 },
      saydamlik: 0.8,
      kirilmaIndisi: 1.33,
      absorpsiyonRenk: { r: 0.2, g: 0.2, b: 0.4, a: 1.0 },
      absorpsiyonMesafe: 1.0,
      sicaklikBagimliligi: false,
      arrheniusB: 0,
      arrheniusRefSicaklik: 20.0,
      ozel: true
    };
    this.malzemeler.set(id, yeniMalzeme);
    return yeniMalzeme;
  }

  /**
   * Clone a material as custom
   */
  klonla(id: string, yeniAd: string): SiviMalzeme | null {
    const kaynak = this.malzemeler.get(id);
    if (!kaynak) return null;

    const klonId = 'ozel_' + Date.now();
    const klon: SiviMalzeme = {
      ...kaynak,
      id: klonId,
      ad: yeniAd,
      adEN: yeniAd,
      kategori: MalzemeKategori.OZEL,
      ozel: true
    };

    this.malzemeler.set(klon.id, klon);
    return klon;
  }

  /**
   * Calculate viscosity at given temperature using Arrhenius model
   * mu(T) = mu_ref * exp(B * (1/T - 1/T_ref))
   */
  viskoziteHesapla(malzeme: SiviMalzeme, sicaklik: number): number {
    if (!malzeme.sicaklikBagimliligi) {
      return malzeme.viskozite;
    }

    const T = sicaklik + 273.15;  // Convert to Kelvin
    const T_ref = malzeme.arrheniusRefSicaklik + 273.15;

    // Arrhenius equation
    const mu = malzeme.viskozite * Math.exp(
      malzeme.arrheniusB * (1.0 / T - 1.0 / T_ref)
    );

    return mu;
  }

  /**
   * Calculate surface tension at temperature (linear approximation)
   * sigma(T) = sigma_ref * (1 - 0.001 * (T - T_ref))
   */
  yuzeyGerilimiHesapla(malzeme: SiviMalzeme, sicaklik: number): number {
    const deltaT = sicaklik - malzeme.sicaklik;
    // Typical surface tension decreases ~0.1% per degree
    const sigma = malzeme.yuzeyGerilimi * (1.0 - 0.001 * deltaT);
    return Math.max(0.001, sigma); // Minimum 0.001 N/m
  }

  /**
   * Export custom materials to JSON string
   */
  exportJSON(): string {
    const ozelMalzemeler = this.ozeller();
    return JSON.stringify(ozelMalzemeler, null, 2);
  }

  /**
   * Import custom materials from JSON string
   * Returns number of materials imported
   */
  importJSON(json: string): number {
    try {
      const malzemeler: SiviMalzeme[] = JSON.parse(json);
      let eklenen = 0;

      for (const m of malzemeler) {
        // Force custom flag and generate new ID
        m.ozel = true;
        m.id = 'ozel_' + Date.now() + '_' + eklenen;
        m.kategori = MalzemeKategori.OZEL;

        // Validate required fields
        if (!m.ad || typeof m.yogunluk !== 'number' || typeof m.viskozite !== 'number') {
          console.warn('Skipping invalid material:', m);
          continue;
        }

        this.malzemeler.set(m.id, m);
        eklenen++;
      }

      return eklenen;
    } catch (e) {
      console.error('Failed to import materials:', e);
      return 0;
    }
  }

  /**
   * Get material count
   */
  sayi(): number {
    return this.malzemeler.size;
  }

  /**
   * Get preset count
   */
  presetSayisi(): number {
    return this.presetler().length;
  }

  /**
   * Get custom count
   */
  ozelSayisi(): number {
    return this.ozeller().length;
  }

  /**
   * Search materials by name (Turkish or English)
   */
  ara(arama: string): SiviMalzeme[] {
    const lower = arama.toLowerCase();
    return this.hepsi().filter(m =>
      m.ad.toLowerCase().includes(lower) ||
      m.adEN.toLowerCase().includes(lower)
    );
  }

  /**
   * Get materials suitable for a temperature range
   */
  sicaklikAraligindakiler(minSicaklik: number, maxSicaklik: number): SiviMalzeme[] {
    return this.hepsi().filter(m => {
      // Material must be liquid in this range
      if (m.solidusSicaklik > -999) {
        // Has solidification
        return m.liquidusSicaklik >= minSicaklik && m.solidusSicaklik <= maxSicaklik;
      } else {
        // No solidification - check if default temp is in range
        return m.sicaklik >= minSicaklik - 100 && m.sicaklik <= maxSicaklik + 100;
      }
    });
  }

  /**
   * Reset to presets only (remove all custom materials)
   */
  sifirla(): void {
    const presets = this.presetler();
    this.malzemeler.clear();
    for (const p of presets) {
      this.malzemeler.set(p.id, p);
    }
  }
}

/**
 * Singleton default instance
 */
let _varsayilanKutuphane: MalzemeKutuphanesi | null = null;

/**
 * Get the global material library instance
 */
export function getMalzemeKutuphanesi(): MalzemeKutuphanesi {
  if (!_varsayilanKutuphane) {
    _varsayilanKutuphane = new MalzemeKutuphanesi();
  }
  return _varsayilanKutuphane;
}

/**
 * Reset the global material library (for testing)
 */
export function resetMalzemeKutuphanesi(): void {
  _varsayilanKutuphane = null;
}
